import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import itemStyles from './TransactionItem/TransactionItem.module.css'

import {
    deepCopy,
    getRecurrenceDisplayString,
} from '../utilities/general-utils'
import TransactionItemDetail from './TransactionItem/TransactionItemDetail'
import { IconAngleRightBold, IconSnowball } from '../assets/icons'

const sortTypes = {
    ascending: {
        class: 'ascending',
        fn: (a, b) => (typeof a === 'string' ? a.localeCompare(b) : a - b),
    },
    descending: {
        class: 'descending',
        fn: (a, b) => (typeof a === 'string' ? b.localeCompare(a) : b - a),
    },
    default: {
        class: 'default',
        fn: (a, b) => (typeof a === 'string' ? a.localeCompare(b) : a - b),
    },
}

const TransactionsTableSortable = ({items, snowballParam}) => {
    const rows = items.map((item) => {
        return {
            ...item,
            frequency: getRecurrenceDisplayString(item),
            frequencySortable: getRecurrenceDisplayString(item, true),
        }
    })

    const [filterHasChanged, setFilterHasChanged] = useState(false)
    const [currentSortField, setCurrentSortField] = useState('title')
    const [filterTerm, setFilterTerm] = useState('')
    const [sortedRows, setRows] = useState(rows)
    const hasSnowballItems = rows.some((item) => item.is_snowball === 1)

    const onSortChange = (currentSort, field, valType) => {
        let nextSort
        let rowsCopy = deepCopy(sortedRows);

        if (currentSort === 'default') nextSort = 'ascending'
        else if (currentSort === 'ascending') nextSort = 'descending'
        else if (currentSort === 'descending') nextSort = 'default'

        if (valType === 'decimal') {
            rowsCopy.sort((a, b) =>
                sortTypes[nextSort].fn(
                    parseFloat(a[field]),
                    parseFloat(b[field]),
                ),
            )
        } else if (valType === 'int') {
            rowsCopy.sort((a, b) =>
                sortTypes[nextSort].fn(parseInt(a[field]), parseInt(b[field])),
            )
        } else if (valType === 'date') {
            rowsCopy.sort((a, b) =>
                sortTypes[nextSort].fn(new Date(a[field]), new Date(b[field])),
            )
        } else if (valType === 'string') {
            rowsCopy.sort((a, b) =>
                sortTypes[nextSort].fn(
                    a[field].toLowerCase(),
                    b[field].toLowerCase(),
                ),
            )
        }

        setCurrentSortField(field)
        setRows(rowsCopy)
        return nextSort
    }

    const filter = (e) => {
        let rowsCopy = deepCopy(rows);
        const keyword = e.target.value;

        if (keyword) {
            const results = rowsCopy.filter((item) => {
                return item.title.toLowerCase().indexOf(keyword.toLowerCase()) > -1;
            });
            setRows(results);
        } else {
            setRows(rows)
        }

        setFilterTerm(keyword)

        if (keyword.length < filterTerm.length) {
            setFilterHasChanged(true)
        }
    }

    const snowballFilter = (e) => {
        let rowsCopy = deepCopy(rows);
        const checked = e.target.checked;

        if (checked) {
            const results = rowsCopy.filter((item) => {
                return item.is_snowball === 1;
            });
            setRows(results);
        }

        if (!checked) {
            setRows(rows)
        }
    }

    useEffect(() => {
        if (snowballParam) {
            const results = rows.filter((item) => {
                return item.is_snowball === 1;
            });
            setRows(results);
        }
    }, [])

    useEffect(() => {
        if (filterHasChanged) {
            setFilterHasChanged(false)
        }
    }, [currentSortField, filterTerm, filterHasChanged, sortedRows])

    return (
        <div className="content-width-container--slim">
            <div className="formField text-left" style={{maxWidth: '100%'}}>
                <label>
                    Filter by Title
                </label>
                <input
                    type="search"
                    value={filterTerm}
                    onChange={filter}
                    className="input"
                    placeholder="search terms..."
                />
            </div>

            {hasSnowballItems && (
                <>
                    <div className="formField formField-inline-checkbox">
                        <input
                            type="checkbox"
                            id="snowballFilter"
                            defaultChecked={snowballParam}
                            onChange={snowballFilter}
                        />
                        <label htmlFor="snowballFilter">
                            <IconSnowball /> Show only snowball transactions
                        </label>
                    </div>

                    <Link to="/tools/snowball-schedule" className="text-link--red">
                        View Your Snowball Plan <IconAngleRightBold fill="#ff4433" />
                    </Link>
                </>
            )}

            <div className="sortButtonRow">
                <div className="formField" style={{width: "auto"}}>
                    <label style={{marginRight: '1rem'}}>
                        Sort by:{' '}
                    </label>
                </div>

                <SortButton
                    resetToDefault={filterHasChanged}
                    valType="string"
                    field="title"
                    onSortChange={onSortChange}
                >
                    Title
                </SortButton>

                <SortButton
                    resetToDefault={filterHasChanged}
                    valType="string"
                    field="category"
                    onSortChange={onSortChange}
                >
                    Category
                </SortButton>

                <SortButton
                    resetToDefault={filterHasChanged}
                    valType="string"
                    field="frequencySortable"
                    onSortChange={onSortChange}
                >
                    Frequency
                </SortButton>

                <SortButton
                    resetToDefault={filterHasChanged}
                    valType="date"
                    field="start_date"
                    onSortChange={onSortChange}
                >
                    Start Date
                </SortButton>

                <SortButton
                    resetToDefault={filterHasChanged}
                    valType="date"
                    field="end_date"
                    onSortChange={onSortChange}
                >
                    End Date
                </SortButton>

                <SortButton
                    resetToDefault={filterHasChanged}
                    valType="decimal"
                    field="amount"
                    onSortChange={onSortChange}
                >
                    Amount
                </SortButton>
            </div>


            <div className={`${itemStyles.itemsWrap} ${itemStyles.itemsWrapDetail}`}>
            {
                sortedRows ? sortedRows.map(item => <TransactionItemDetail key={item.id + item.instanceDate} item={item} />) : null
            }
            </div>
        </div>
    )
}

const SortButton = ({ field, onSortChange, valType, resetToDefault, children }) => {
    const DEFAULT = 'default';
    const [currentSort, setCurrentSort] = useState(DEFAULT)

    const handleClick = () => {
        setCurrentSort(onSortChange(currentSort, field, valType))
    }

    if (resetToDefault && currentSort !== DEFAULT) setCurrentSort(DEFAULT)

    useEffect(() => {}, [currentSort])

    return (
        <button
            onClick={handleClick}
            className={`sortButton ${currentSort}`}
            title={currentSort}
        >
            {children}
        </button>
    )
}

export default TransactionsTableSortable
